// ── "Project Manager assignment" pre-alert ───────────────────────────────────
// Sent when a turnover is submitted for a job number (alongside the Job
// Number Requested email to Accounting), so the VP of Construction can pick
// the Project Manager while Accounting assigns the number.
//
// Recipients = the PM Assignment Alert list on Turnover Configuration
// (TurnoverConfigContacts, GroupKey 'pm_assignment_alert'). Nothing is sent
// while that list is empty — there is no hardcoded fallback address.
// Pure: builds the message; the caller sends it. Every value is HTML-escaped.

export const PM_ALERT_GROUP = "pm_assignment_alert";
export const PLATFORM_BASE_URL = "https://estimating.cleanspaceus.com";

function esc(v) {
  return String(v ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

export function pmAlertRecipients(configContacts = []) {
  const rows = (configContacts || []).filter(c => c.GroupKey === PM_ALERT_GROUP && c.Email?.trim());
  const seen = new Set();
  const pick = (role) => rows.filter(c => c.RoleLabel === role).map(c => {
    const e = c.Email.trim();
    if (seen.has(e.toLowerCase())) return null;
    seen.add(e.toLowerCase());
    return { name: c.Name || e, email: e };
  }).filter(Boolean);
  const to = pick("To");
  const cc = pick("CC");
  if (to.length === 0 && cc.length > 0) to.push(cc.shift());
  return { to, cc };
}

const money = (v) => {
  const n = Number(String(v ?? "").replace(/[$,\s]/g, ""));
  return Number.isFinite(n) && String(v ?? "").trim() !== "" ? "$" + Math.round(n).toLocaleString("en-US") : "—";
};
const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(String(d).slice(0, 10) + "T12:00:00");
  return Number.isNaN(dt.getTime()) ? "—" : dt.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

export function buildPmAssignmentEmail({ turnover = {}, submittedBy = "" }) {
  const project = esc(turnover.ProjectName) || "this project";
  const url = `${PLATFORM_BASE_URL}/turnover/${Number.parseInt(turnover.TurnoverID, 10) || ""}?section=1`;
  const value = turnover.ContractValue || turnover.DealAmount;
  const sf = Number(turnover.SquareFeet);
  const td = "padding:6px 12px;border:1px solid #e5e7eb;font-size:13px;vertical-align:top";
  const th = `${td};background:#f8f9fb;font-weight:600;width:170px`;
  const row = (k, v) => `<tr><td style="${th}">${k}</td><td style="${td}">${v}</td></tr>`;
  const description = String(turnover.DealDescription || "").trim();
  const pm = String(turnover.ProjectManager || "").trim();

  const html = `
<div style="font-family:Arial,sans-serif;max-width:680px;color:#111">
  <div style="background:#1e3a5f;padding:18px 26px;border-radius:8px 8px 0 0">
    <div style="color:#fff;font-size:18px;font-weight:700">Project Manager Needed</div>
    <div style="color:#93c5fd;font-size:13px;margin-top:4px">${project}${turnover.ClientName ? ` — ${esc(turnover.ClientName)}` : ""}</div>
  </div>
  <div style="border:1px solid #e5e7eb;border-top:none;border-radius:0 0 8px 8px;padding:22px 26px;font-size:14px;line-height:1.55">
    <p style="margin:0 0 16px">${submittedBy ? `${esc(submittedBy)} submitted` : "This turnover was submitted"} <strong>${project}</strong> for a job number — the contract is executed and Accounting is assigning the number now. Please assign a Project Manager so the turnover can proceed.</p>

    <div style="background:#fff7ed;border:1px solid #fdba74;border-radius:6px;padding:14px 16px;margin:0 0 18px">
      <div style="font-weight:700;color:#9a3412;margin-bottom:6px">Action needed — assign the Project Manager</div>
      <div style="margin-bottom:12px">${pm
        ? `Sales has <strong>${esc(pm)}</strong> on the Cover Sheet. Confirm, or change it on the Cover Sheet.`
        : "No Project Manager is assigned yet. Choose one on the turnover's Cover Sheet."}</div>
      <a href="${esc(url)}" style="display:inline-block;background:#1e3a5f;color:#fff;padding:10px 22px;border-radius:6px;font-size:14px;font-weight:700;text-decoration:none">Open the Cover Sheet →</a>
    </div>

    <div style="font-size:13px;font-weight:700;margin:0 0 6px">Project description</div>
    <div style="background:#f8f9fb;border:1px solid #e5e7eb;border-radius:6px;padding:12px 16px;margin:0 0 18px;font-size:13px;white-space:pre-wrap">${description ? esc(description) : "<em>No description on the HubSpot deal.</em>"}</div>

    <table style="width:100%;border-collapse:collapse;margin:0 0 6px">
      ${row("Client", esc(turnover.ClientName) || "—")}
      ${row("Location", esc(turnover.CityState) || "—")}
      ${row("Market / Type", [turnover.Market, turnover.ProjectType].filter(Boolean).map(esc).join(" · ") || "—")}
      ${row("Contract", esc(turnover.ContractStructure) || "—")}
      ${row("Labor", esc(turnover.LaborStrategy) || "—")}
      ${row("Square feet", Number.isFinite(sf) && sf > 0 ? `${sf.toLocaleString("en-US")} SF` : "—")}
      ${row("Contract value", money(value))}
      ${row("Contract signed", fmtDate(turnover.ContractExecutedDate))}
      ${row("Planned turnover", fmtDate(turnover.PlannedTurnoverDate))}
      ${row("Sales lead", esc(turnover.Salesperson) || "—")}
      ${row("Estimator", esc(turnover.Estimator) || "—")}
    </table>
  </div>
</div>`;

  return {
    subject: `Project Manager needed — ${turnover.ProjectName || "Turnover"}${turnover.ClientName ? ` (${turnover.ClientName})` : ""}`,
    html,
  };
}
